import { useEffect, useRef } from "react";

/**
 * ShortcutsOverlay — modal dialog listing every shortcut from
 * specs/001-mvp-foundation/contracts/ui-keyboard.md.
 *
 * Opened by `?`, closed by `Escape` or a click outside / on the close
 * button. Uses a native <dialog> for accessibility (focus trap, scrim).
 */
export interface ShortcutsOverlayProps {
  open: boolean;
  onClose: () => void;
}

const ROWS: Array<{ keys: string[]; action: string }> = [
  { keys: ["Space", "K"], action: "Play / pause" },
  { keys: ["J"], action: "Seek backward 10s" },
  { keys: ["L"], action: "Seek forward 10s" },
  { keys: ["←"], action: "Previous item in queue" },
  { keys: ["→"], action: "Next item in queue" },
  { keys: ["F"], action: "Toggle fullscreen" },
  { keys: ["P"], action: "Toggle picture-in-picture" },
  { keys: ["M"], action: "Toggle mute" },
  { keys: ["?"], action: "Show / hide this overlay" },
  { keys: ["Esc"], action: "Close overlay / exit fullscreen" },
];

export function ShortcutsOverlay({ open, onClose }: ShortcutsOverlayProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    ref.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="shortcuts-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="shortcuts-overlay-dialog" ref={ref} tabIndex={-1}>
        <h2>Keyboard shortcuts</h2>
        <table>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.action}>
                <td>
                  {row.keys.map((k, i) => (
                    <span key={k}>
                      {i > 0 && " "}
                      <kbd>{k}</kbd>
                    </span>
                  ))}
                </td>
                <td>{row.action}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: "1rem", textAlign: "right" }}>
          <button onClick={onClose} autoFocus>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
